"use client";
import { cn } from "@/lib/utils";
import { useState, useEffect, useRef, useCallback, ReactNode } from "react";

type Props = {
  children: ReactNode;
  forceShow?: boolean;
  className?: string;
  reactTop?: number;
};
export default function StickyCTAWrapper({
  children,
  forceShow = false,
  className,
  reactTop = 25,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [isNavOnTop, setIsNavOnTop] = useState(false);

  const onScroll = useCallback(() => {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const buffer = 10;
    const newIsNavOnTop = rect.top <= reactTop + buffer;

    setIsNavOnTop((prev) => (prev !== newIsNavOnTop ? newIsNavOnTop : prev));
  }, [reactTop]);

  useEffect(() => {
    if (forceShow) {
      setIsNavOnTop(true);
      return;
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onScroll, forceShow]);

  // Keep --sticky-cta-height in sync with the actual rendered banner height.
  // Set an immediate initial value of 0px so dependant layout is correct
  // before the first ResizeObserver callback fires, and reset to 0px on
  // cleanup so the variable doesn't bleed into the next page after
  // client-side navigation.
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    // Initialise synchronously so there is never a frame where the variable
    // holds a stale value from a previous mount.
    const currentHeight = isNavOnTop ? el.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty(
      "--sticky-cta-height",
      `${currentHeight}px`,
    );

    const observer = new ResizeObserver(() => {
      const h = isNavOnTop ? el.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty(
        "--sticky-cta-height",
        `${h}px`,
      );
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty("--sticky-cta-height", "0px");
    };
  }, [isNavOnTop]);

  return (
    <div
      className={cn(
        "block sticky top-0 w-screen ml-[calc(-50vw+50%)] bg-primary z-50",
        className,
      )}
      style={{
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        opacity: isNavOnTop ? 1 : 0,
        visibility: isNavOnTop ? "visible" : "hidden",
        pointerEvents: isNavOnTop ? "auto" : "none",
        height: isNavOnTop ? "auto" : 0,
        overflow: isNavOnTop ? "visible" : "hidden",
      }}
      ref={ref}
    >
      {children}
    </div>
  );
}
